import React, { useState } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import { UserProfile } from '../../types/user';
import { AdvancedLifeAnalysisResult, LifeThemeId, LifeThemeQuestion } from '../../types/advancedLifeAnalysis';
import { LIFE_THEMES_REGISTRY } from '../../utils/advancedLifeAnalysisTaxonomy';

// Reusable components
import { LifeStageContextBanner } from '../LifeStageContextBanner';
import { NatalPromiseVerdictCard } from '../NatalPromiseVerdictCard';
import { AdvancedChartRail } from '../AdvancedChartRail';
import { AstrologicalEvidenceGrid } from '../AstrologicalEvidenceGrid';
import { DashaGocharConvergenceCard } from '../DashaGocharConvergenceCard';
import { PhalaBhutaTimeline } from '../PhalaBhutaTimeline';
import { ForensicCalculationAccordion } from '../ForensicCalculationAccordion';
import { RemediesGuidanceCard } from '../RemediesGuidanceCard';
import { AcharyaSanctuaryContainer } from '../AcharyaSanctuaryContainer';
import { ChartViewer } from '../ChartViewer';
import { AstrologerDiagnosticDossier } from '../AstrologerDiagnosticDossier';
import { GemstoneAdvisor } from '../GemstoneAdvisor';
import { FiveRuleHouseCard } from '../v517/FiveRuleHouseCard';
import { QualityDistributionCard } from '../v517/QualityDistributionCard';
import { EnhancedRemedyRecommendations } from '../v517/EnhancedRemedyRecommendations';
import { D9DeepAnalysisModal } from '../v517/D9DeepAnalysisModal';
import { TemporalWindowCard } from '../v518/TemporalWindowCard';
import { VargaConvergenceCard } from '../v518/VargaConvergenceCard';
import { HoraRemedyScheduler } from '../v518/HoraRemedyScheduler';
import { KundaliMilanModal } from '../v518/KundaliMilanModal';
import { LifeDomainMuhurtaAid } from './LifeDomainMuhurtaAid';
import { CurrentPhaseForecastView } from '../CurrentPhaseForecastView';
import { CareerBusinessVsJobModule } from '../CareerBusinessVsJobModule';

// V5.19 Predictive & Methodological Intelligence Components
import {
  PredictiveEventTimelineCard,
  TriangulationMatrixCard,
  LifeMilestoneCalibrationCard,
  ClassicalEvidenceDrawer,
  MultiChartComparatorModal
} from '../v519';

import {
  Compass,
  HelpCircle,
  Sparkles,
  BookOpen,
  Calculator,
  Shield,
  Award,
  Layers,
  Activity,
  Briefcase,
  Heart,
  Coins,
  Home,
  Car,
  Baby,
  GraduationCap,
  TrendingUp,
  Globe,
  Scale,
  Users,
  LineChart,
  Wrench,
  Target,
  FileText
} from 'lucide-react';

interface ProAnalysisViewProps {
  kundali: CompleteKundali;
  analysisResult: AdvancedLifeAnalysisResult;
  selectedThemeId: LifeThemeId;
  onSelectTheme: (id: LifeThemeId) => void;
  selectedQuestionId: string;
  onSelectQuestion: (id: string) => void;
  lang: Language;
  chartStyle: ChartStyle;
  setChartStyle: (style: ChartStyle) => void;
  onOpenReasoningModal: () => void;
  onOpenPrintReport: (mode?: 'BASIC' | 'LEARNER' | 'PRO' | 'ACHARYA') => void;
  onEditKundali?: () => void;
  aiQuota: { remaining: number; allowance: number; resetDate?: string } | null;
  customQuestionInput: string;
  onCustomQuestionChange: (val: string) => void;
  onRequestAiAnalysis: () => void;
  isAiLoading: boolean;
  aiInterpretation: string;
  aiError: string;
  proTab?: 'tools' | 'synthesis' | 'dossier';
  onSelectProTab?: (tab: 'tools' | 'synthesis' | 'dossier') => void;
}

const THEME_ICONS: Record<LifeThemeId, React.ComponentType<{ className?: string }>> = {
  career_job: Briefcase,
  marriage_relationship: Heart,
  wealth_finance: Coins,
  house_property: Home,
  vehicle: Car,
  children: Baby,
  education: GraduationCap,
  health_vitality: Activity,
  business: TrendingUp,
  foreign_travel: Globe,
  litigation_competition: Scale,
  spirituality: Sparkles,
  family_parents: Users
};

export const ProAnalysisView: React.FC<ProAnalysisViewProps> = ({
  kundali,
  analysisResult,
  selectedThemeId,
  onSelectTheme,
  selectedQuestionId,
  onSelectQuestion,
  lang,
  chartStyle,
  setChartStyle,
  onOpenReasoningModal,
  onOpenPrintReport,
  onEditKundali,
  aiQuota,
  customQuestionInput,
  onCustomQuestionChange,
  onRequestAiAnalysis,
  isAiLoading,
  aiInterpretation,
  aiError,
  proTab: externalProTab,
  onSelectProTab
}) => {
  const isHi = lang === 'hi';
  const allThemes = Object.values(LIFE_THEMES_REGISTRY);
  const currentTheme = LIFE_THEMES_REGISTRY[selectedThemeId] || allThemes[0];

  const proTab = externalProTab ?? 'tools';
  const [isMilanModalOpen, setIsMilanModalOpen] = useState<boolean>(false);
  const [isD9ModalOpen, setIsD9ModalOpen] = useState<boolean>(false);
  const [isEvidenceDrawerOpen, setIsEvidenceDrawerOpen] = useState<boolean>(false);
  const [isComparatorModalOpen, setIsComparatorModalOpen] = useState<boolean>(false);
  const [evidenceRuleId, setEvidenceRuleId] = useState<string | undefined>(undefined);

  return (
    <div
      id="pro-presentation-view"
      className="space-y-6 animate-fadeIn"
      role="tabpanel"
      aria-labelledby="role-tab-pro"
    >
      {proTab === 'tools' ? (
        <div className="bg-astro-bg border border-astro-border rounded-3xl p-4 sm:p-6 shadow-sm">
          <ChartViewer
            kundali={kundali}
            chartStyle={chartStyle}
            setChartStyle={setChartStyle}
            lang={lang}
            onEditProfile={onEditKundali}
          />
        </div>
      ) : proTab === 'dossier' ? (
        <div className="bg-astro-bg border border-astro-border rounded-3xl p-4 sm:p-6 shadow-sm">
          <AstrologerDiagnosticDossier
            kundali={kundali}
            lang={lang}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* 1. Full Life-Stage Context Banner */}
          <LifeStageContextBanner
            lifeStageContext={analysisResult.lifeStageContext}
            lang={lang}
          />


          {/* 2. 13-Theme Horizontal Selector */}
          <div
            id="pro-theme-selector"
            className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-5 shadow-2xs space-y-3"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-astro-accent" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                  {isHi ? '१३ शास्त्रीय जीवन क्षेत्र (थीम चयन)' : '13 Classical Life Themes (Select Domain)'}
                </h3>
              </div>
              <span className="text-[11px] text-astro-secondary-text">
                {allThemes.length} {isHi ? 'विषय उपलब्ध' : 'Themes'}
              </span>
            </div>

            {/* Horizontal Scrolling Tabs */}
            <div className="w-full overflow-x-auto custom-horizontal-scrollbar pb-2">
              <div className="flex items-center gap-2 min-w-max px-0.5">
                {allThemes.map((theme) => {
                const Icon = THEME_ICONS[theme.id] || Sparkles;
                const isSelected = selectedThemeId === theme.id;

                return (
                  <button
                    key={theme.id}
                    onClick={() => onSelectTheme(theme.id)}
                    className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap border transition-all min-h-[44px] touch-manipulation cursor-pointer ${
                      isSelected
                        ? 'bg-astro-primary text-white border-astro-primary shadow-2xs ring-2 ring-astro-accent/30'
                        : 'bg-astro-bg border-astro-border text-astro-text hover:border-astro-accent'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isSelected ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
                    <span>{isHi ? theme.nameHi : theme.nameEn}</span>
                  </button>
                );
              })}
              </div>
            </div>
          </div>

          {/* 3. Target Question Controls with Action Buttons */}
          <div
            id="pro-question-selector"
            className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-5 shadow-2xs space-y-3.5"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-astro-border pb-2.5">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-astro-accent" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                  {isHi ? 'विशिष्ट जीवन प्रश्न (लक्षित विश्लेषण)' : 'Specific Life Questions (Target Inquiry)'}
                </h3>
              </div>

              <div className="flex items-center gap-2 self-end sm:self-auto flex-wrap">
                {/* Multi-Chart Comparator Trigger */}
                <button
                  onClick={() => setIsComparatorModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-astro-bg hover:bg-astro-secondary-surface border border-astro-border text-astro-primary text-xs font-bold transition-all min-h-[38px] touch-manipulation cursor-pointer"
                >
                  <Users className="w-3.5 h-3.5 text-astro-primary" />
                  <span>{isHi ? 'बहु-कुंडली तुलना' : 'Chart Comparator'}</span>
                </button>

                {/* Classical Shastra Evidence Drawer Trigger */}
                <button
                  onClick={() => {
                    setEvidenceRuleId(undefined);
                    setIsEvidenceDrawerOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-astro-bg hover:bg-astro-secondary-surface border border-astro-border text-astro-primary text-xs font-bold transition-all min-h-[38px] touch-manipulation cursor-pointer"
                >
                  <BookOpen className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'शास्त्रीय सूत्र कोष' : 'Shastra Sutras'}</span>
                </button>

                {/* Ashtakoota Kundali Milan Modal Trigger */}
                <button
                  onClick={() => setIsMilanModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-pink-50 hover:bg-pink-100 dark:bg-pink-950/40 border border-pink-200 text-pink-700 dark:text-pink-300 text-xs font-bold transition-all min-h-[38px] touch-manipulation cursor-pointer"
                  title={isHi ? 'अष्टकूट मिलान (३६ गुण) एवं मांगलिक परिहार' : 'Ashtakoota Milan (36 Gunas) & Synastry'}
                >
                  <Heart className="w-3.5 h-3.5 text-pink-500" />
                  <span>{isHi ? 'अष्टकूट मिलान (३६ गुण)' : 'Ashtakoota Milan'}</span>
                </button>

                {/* D9 Navamsha & D60 Shashtiamsha Deep Analysis Trigger */}
                <button
                  onClick={() => setIsD9ModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900 text-xs font-bold transition-all min-h-[38px] touch-manipulation cursor-pointer"
                  title={isHi ? 'गहन नवांश (D9) एवं षष्ट्यंश (D60) विश्लेषण' : 'D9 Navamsha & D60 Shashtiamsha Deep Intelligence'}
                >
                  <Layers className="w-3.5 h-3.5 text-amber-700" />
                  <span>{isHi ? 'D9 एवं D60 विश्लेषण' : 'D9 & D60 Analysis'}</span>
                </button>

                {/* Why This Conclusion Trigger Button */}
                <button
                  onClick={onOpenReasoningModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-astro-bg hover:bg-astro-secondary-surface border border-astro-border text-astro-primary text-xs font-bold transition-all min-h-[38px] touch-manipulation cursor-pointer"
                >
                  <BookOpen className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'यह निष्कर्ष क्यों?' : 'Why This Conclusion?'}</span>
                </button>
              </div>
            </div>

            {/* Question Selector Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {currentTheme.questions.map((q: LifeThemeQuestion) => {
                const isSelected = selectedQuestionId === q.id;

                return (
                  <button
                    key={q.id}
                    onClick={() => onSelectQuestion(q.id)}
                    className={`p-3 rounded-xl border text-left text-xs transition-all min-h-[52px] flex items-start gap-2.5 touch-manipulation cursor-pointer ${
                      isSelected
                        ? 'bg-astro-bg border-astro-primary shadow-2xs ring-1 ring-astro-primary font-bold text-astro-primary'
                        : 'bg-astro-bg border-astro-border text-astro-text hover:border-astro-accent'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full mt-1 shrink-0 ${isSelected ? 'bg-astro-primary' : 'bg-astro-border'}`} />
                    <span className="leading-snug">{isHi ? q.questionHi : q.questionEn}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 4. Natal Promise & Key Finding Verdict Card with Analytical Index */}
          <NatalPromiseVerdictCard
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* V5.17 Five-Rule House Analysis Engine */}
          <FiveRuleHouseCard
            kundali={kundali}
            themeId={selectedThemeId}
            lang={lang}
          />

          {/* V5.17 Quality Archetype & Tri-Zone Quadrant Distribution */}
          <QualityDistributionCard
            kundali={kundali}
            lang={lang}
          />

          {/* V5.18 Multi-Varga Convergence Matrix */}
          <VargaConvergenceCard
            kundali={kundali}
            themeId={selectedThemeId}
            lang={lang}
          />

          {/* 5. Synchronized Divisional Chart Rail (D1, Thematic Varga, D9) with Full Varga Override */}
          <AdvancedChartRail
            kundali={kundali}
            relevantVargas={analysisResult.relevantVargas}
            lang={lang}
            chartStyle={chartStyle}
            onStyleChange={setChartStyle}
          />

          {/* 6. Three-Column Categorical Astrological Evidence Grid */}
          <AstrologicalEvidenceGrid
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* 7. Dasha + Gochar Convergence Matrix */}
          <DashaGocharConvergenceCard
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* Career: Business vs Job & Predictive Timings */}
          {(selectedThemeId === 'career_job' || selectedThemeId === 'business') && (
            <CareerBusinessVsJobModule
              kundali={kundali}
              lang={lang}
            />
          )}

          {/* V5.18 Forward Temporal Windows & Quarterly Timelines */}
          <TemporalWindowCard
            kundali={kundali}
            themeId={selectedThemeId}
            lang={lang}
          />

          {/* Life Domain Auspicious Timing & Actionable Muhurta Aid */}
          <LifeDomainMuhurtaAid
            kundali={kundali}
            themeId={selectedThemeId}
            lang={lang}
          />

          {/* V5.19 Discrete Predictive Event Timing Windows */}
          <PredictiveEventTimelineCard
            kundali={kundali}
            lang={lang}
            presentationMode="PRO"
            onOpenEvidenceDrawer={(evt, ruleId) => {
              setEvidenceRuleId(ruleId);
              setIsEvidenceDrawerOpen(true);
            }}
            onOpenConsultation={(prompt) => {
              if (onCustomQuestionChange) onCustomQuestionChange(prompt);
              const el = document.getElementById('acharya-sanctuary-container');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
          />

          {/* V5.19 Three-Stream Methodological Triangulation Matrix */}
          <TriangulationMatrixCard
            kundali={kundali}
            lang={lang}
            presentationMode="PRO"
            onOpenEvidenceDrawer={(evt, ruleId) => {
              setEvidenceRuleId(ruleId);
              setIsEvidenceDrawerOpen(true);
            }}
          />

          {/* V5.19 Longitudinal Life Milestone & Timeline Calibration */}
          <LifeMilestoneCalibrationCard
            kundali={kundali}
            lang={lang}
            presentationMode="PRO"
          />

          {/* 8. Phala-Bhuta Timeline */}
          <PhalaBhutaTimeline
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* 9. Forensic Calculations Accordion (Collapsed) */}
          <ForensicCalculationAccordion
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* 10. Remedies & Guidance Card */}
          <RemediesGuidanceCard
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* V5.17 Categorized Remedies Bank with Multi-Layer Sound Synthesizer */}
          <EnhancedRemedyRecommendations
            kundali={kundali}
            themeId={selectedThemeId}
            lang={lang}
            userEntitlement="PREMIUM"
          />

          {/* V5.18 Planetary Hora & Remedial Auspicious Timers */}
          <HoraRemedyScheduler
            kundali={kundali}
            lang={lang}
          />

          {/* 11. Acharya Sanctuary Container (AI Consultation) */}
          <AcharyaSanctuaryContainer
            lang={lang}
            aiQuota={aiQuota}
            customQuestionInput={customQuestionInput}
            onCustomQuestionChange={onCustomQuestionChange}
            onRequestAiAnalysis={onRequestAiAnalysis}
            isAiLoading={isAiLoading}
            aiInterpretation={aiInterpretation}
            aiError={aiError}
          />
        </div>
      )}

      {/* V5.18 Kundali Milan (36-Guna Synastry) Modal */}
      <KundaliMilanModal
        isOpen={isMilanModalOpen}
        onClose={() => setIsMilanModalOpen(false)}
        primaryKundali={kundali}
        lang={lang}
      />

      {/* V5.17 D9 & D60 Deep Analysis Modal */}
      <D9DeepAnalysisModal
        isOpen={isD9ModalOpen}
        onClose={() => setIsD9ModalOpen(false)}
        kundali={kundali}
        lang={lang}
        chartStyle={chartStyle}
      />

      {/* V5.19 Classical Shastra Evidence Drawer */}
      <ClassicalEvidenceDrawer
        isOpen={isEvidenceDrawerOpen}
        onClose={() => setIsEvidenceDrawerOpen(false)}
        selectedRuleId={evidenceRuleId}
        lang={lang}
        presentationMode="PRO"
      />

      {/* V5.19 Multi-Chart Interpersonal Comparator Modal */}
      <MultiChartComparatorModal
        isOpen={isComparatorModalOpen}
        onClose={() => setIsComparatorModalOpen(false)}
        primaryKundali={kundali}
        lang={lang}
        presentationMode="PRO"
      />
    </div>
  );
};

